import React, { useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { confirmDialog } from 'primereact/confirmdialog';

const ItemType = 'FOLDER';

const FolderNode = ({ folder, depth, onDrop, onAdd, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(folder.label);

  const getTotalChildrenCount = (node) => {
    if (!node.children || node.children.length === 0) return 0;
    return node.children.reduce((acc, child) => acc + 1 + getTotalChildrenCount(child), 0);
  };

  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: ItemType,
    drop: (item, monitor) => {
      if (item.folderId === folder.folderId) return; // Prevent self-drop
      if (monitor.isOver({ shallow: true })) {
        onDrop(item.folderId, folder.folderId);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  const [, dragRef] = useDrag({
    type: ItemType,
    item: { folderId: folder.folderId },
  });

  const nodeRef = (el) => dragRef(dropRef(el));

  return (
    <div ref={nodeRef} className={`folder-node modern card-bg ${isOver && canDrop ? 'highlight-drop' : ''}`}>
      <div className="folder-header">
        <span className="drag-handle">
          <i className="fa fa-grip-vertical"></i>
        </span>
        <span className="folder-icon" onClick={() => setExpanded(!expanded)}>
          <i className={`fa fa-regular text-primary ${expanded ? 'fa-folder-open' : 'fa-folder'}`} style={{ width: '20px' }}></i>
        </span>

        {editing ? (
          <InputText
            className="folder-input"
            value={inputValue}
            onChange={(e) => {
              const filteredValue = e?.target?.value?.replace(/[^a-zA-Z0-9 _-]/g, '');
              setInputValue(filteredValue);
            }}
            onBlur={() => {
              const trimmed = inputValue.trim();
              const finalLabel = trimmed === '' ? 'Untitled' : trimmed;
              onEdit(folder.folderId, finalLabel);
              setInputValue(finalLabel);
              setEditing(false);
            }}
            autoFocus
          />
        ) : (
          <span className="folder-name d-flex align-items-center" style={{ height: 36.5 }} onDoubleClick={() => setEditing(true)}>
            {folder.label}
          </span>
        )}

        <span className="badge-folder">{getTotalChildrenCount(folder)}</span>

        <div className="action-buttons">
          <Button
            icon="pi pi-plus"
            className="p-button-rounded p-button-text p-button-success"
            onClick={() => onAdd(folder.folderId)}
            tooltip="Add Folder"
            tooltipOptions={{ position: 'top' }}
          />
          <Button
            icon="pi pi-trash danger-text"
            className="p-button-rounded p-button-text"
            onClick={() =>
              confirmDialog({
                message: 'Do you want to delete the folder?',
                header: 'Confirm Delete',
                icon: 'pi pi-exclamation-triangle',
                className: 'confirm-delete-popup',
                accept: () => onDelete(folder.folderId),
                reject: () => {},
              })
            }
            tooltip="Delete Folder"
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      </div>

      <div className={`folder-children ${expanded ? 'expanded' : 'collapsed'}`}>
        {folder.children.map((child) => (
          <FolderNode
            key={child.folderId}
            folder={child}
            depth={depth + 1}
            onDrop={onDrop}
            onAdd={onAdd}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
};

export default React.memo(FolderNode);
